// Server-side <title> for the catalog pages (the old SPA set them client-side
// only). Category / content-type names come from the API with a short timeout
// so an unreachable API never delays the page.
import { redirect, render } from "vike/abort"
import { ListCategories, LoadContentTypes, ListProducts, LIST_FIELDS } from "../api"
import { collectCategoryIds } from "../utils/categories"
import { catalogSeedKey, CATALOG_PAGE_SIZE } from "../ui/catalogSeed"
import { langFromPath, stripLang } from "../utils/contentRoutes"
import { breadcrumbJsonLd, seoTexts } from "../utils/seo"

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms))])

const pathOf = (pageContext) => pageContext.urlParsed?.pathname || pageContext.urlPathname || "/"

export async function categoryOnBeforeRender(pageContext) {
  if (pageContext.is404) return {}
  const pathname = pathOf(pageContext)
  const segments = stripLang(pathname).split("/").filter(Boolean)
  const category = segments[0] === "category" ? segments[segments.length - 1] : undefined
  const search = pageContext.urlParsed?.search?.textSearch
  let name = ""
  let list = []
  if (category) {
    try {
      const result = await withTimeout(ListCategories(langFromPath(pathname) || "en"), 3000)
      list = result?.list || []
      name = list.find((c) => c.categoryId === category)?.name || ""
    } catch (error) {
      console.error("Storefront category onBeforeRender:", error?.message || error)
    }
  }
  // a slug a category had before (an imported one, given a slug of ours; a
  // parent renamed, /poreditsi → /series): the address moves to the one it
  // has now, for good — whichever step of the path it is
  if (category && list.some((c) => c.formerIds?.length)) {
    const now = (slug) => (list.some((c) => c.categoryId === slug) ? slug : list.find((c) => c.formerIds?.includes(slug))?.categoryId || slug)
    const moved = segments.map((segment, index) => (index === 0 ? segment : now(segment)))
    if (moved.some((segment, index) => segment !== segments[index])) {
      const language = langFromPath(pathname)
      const query = pageContext.urlParsed?.searchOriginal || ""
      throw redirect(`${language ? `/${language}` : ""}/${moved.join("/")}${query}`, 301)
    }
  }
  // a category the catalog does not have (nor had): a 404 — once the list was
  // read, so an API that did not answer never turns a page into one. The
  // lists that are no category (latest, popular) are made by the API.
  const LISTS = ["latest", "popular"]
  if (category && !name && list.length && !LISTS.includes(category)) throw render(404)

  const language = langFromPath(pathname) || "en"
  const texts = seoTexts(language)
  // no category and no search: the page is the newest of each category (CategoryLatestRows)
  const base = name || (category && LISTS.includes(category) ? category : search ? texts.allProducts : texts.latest)
  const title = search ? `${base}${name ? ", " : " "}"${search}"` : base
  // the trail: every category on the path, by its name
  const steps = segments.slice(1).map((slug, index) => [list.find((c) => c.categoryId === slug)?.name, `/category/${segments.slice(1, index + 2).join("/")}`]).filter(([stepName]) => stepName)
  // the page's products in its HTML: a visitor (and Google, judging the page an ad leads to) sees the books at
  // once, not after the scripts ask for them — the same request the browser makes (CategoryItemBrowser), its
  // answer seeding the first render; the browser still asks again, as before
  let storefrontCategoryList
  if (category && name) {
    const sortBy = pageContext.urlParsed?.search?.sortBy || (search ? "relevance" : "published")
    const page = Math.max(1, parseInt(pageContext.urlParsed?.search?.page) || 1)
    const ids = collectCategoryIds(category, list)
    const request = { page: page - 1, items_per_page: CATALOG_PAGE_SIZE, language, sortBy, sortDirection: -1, owned_only: false, fields: LIST_FIELDS }
    if (search) Object.assign(request, { text_search: search, fallback: true })
    if (ids.length === 1) request.category = ids[0]
    else if (ids.length > 1) request.categories = ids
    try {
      const result = await withTimeout(ListProducts(request), 2500)
      if (result?.list) storefrontCategoryList = { key: catalogSeedKey({ seedFor: category, sortBy, page, textSearch: search || "", language }), result }
    } catch (error) {
      console.error("Storefront category products onBeforeRender:", error?.message || error)
    }
  }
  return {
    storefrontCategoryList,
    title: `${title} | Aldoo`,
    description: texts.category(name || texts.allProducts),
    storefrontCategoryName: name || undefined,
    metadata: steps.length ? { jsonLd: [breadcrumbJsonLd(language, steps)] } : undefined,
  }
}

export async function contentTypeOnBeforeRender(pageContext) {
  const pathname = pathOf(pageContext)
  const contentTypeId = stripLang(pathname).split("/").filter(Boolean)[1]
  if (!contentTypeId) return {}
  let list = []
  try {
    const result = await withTimeout(LoadContentTypes(langFromPath(pathname) || "en"), 3000)
    list = result?.list || []
  } catch (error) {
    console.error("Storefront content type onBeforeRender:", error?.message || error)
    return {}
  }
  const contentType = list.find((ct) => ct.contentId === contentTypeId)
  // the id a type had before the product-type tree (/type/audio-book): the
  // address moves to its profile's, for good
  const moved = !contentType && list.find((ct) => ct.legacyId && ct.legacyId === contentTypeId)
  if (moved) {
    const language = langFromPath(pathname)
    throw redirect(`${language ? `/${language}` : ""}/type/${moved.contentId}${pageContext.urlParsed?.searchOriginal || ""}`, 301)
  }
  const name = contentType?.name
  return name ? { title: `${name} | Aldoo`, storefrontContentTypeName: name } : {}
}
